import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ApiRequestBuilderV2 } from './index';

/**
 * AsyncActionBanner — Reusable polling banner for async actions.
 *
 * Displays a configurable alert banner while an async action is in progress,
 * polls the async_action table for status, and calls onComplete/onFailed when done.
 *
 * Props:
 *   actionId        (string|null)  — The action ID to poll. Banner hidden when null.
 *   message         (string)       — Message to display while pending. Default: "Processing..."
 *   successMessage  (string)       — Message on completion. Default: "Done!"
 *   onComplete      (function)     — Called when action completes. Receives action data.
 *   onFailed        (function)     — Called when action fails. Receives action data.
 *   variant         (string)       — Bootstrap alert variant while pending. Default: "warning"
 *   intervals       (array)        — Poll intervals in ms. Default: [5000, 10000, 20000]
 *   maxAttempts     (number)       — Max poll attempts before giving up. Default: 12
 *   showRefresh     (boolean)      — Show a "Refresh" button on completion. Default: false
 *   onRefresh       (function)     — Called when refresh button is clicked.
 *   autoDismiss     (number|null)  — Auto-dismiss after N ms on complete/fail. Null = no auto-dismiss.
 */
const AsyncActionBanner = ({
    actionId,
    message = 'Processing — please wait...',
    successMessage = 'Done!',
    onComplete,
    onFailed,
    variant = 'warning',
    intervals = [5000, 10000, 20000],
    maxAttempts = 12,
    showRefresh = false,
    onRefresh,
    autoDismiss = 5000,
}) => {
    const [status, setStatus] = useState(null); // null | 'pending' | 'completed' | 'failed' | 'timeout'
    const [actionData, setActionData] = useState(null);
    const [dismissed, setDismissed] = useState(false);
    const attemptRef = useRef(0);
    const timerRef = useRef(null);

    const poll = useCallback(() => {
        if (!actionId) return;

        ApiRequestBuilderV2('asyncActionsActionIdGet', {
            pathParams: { actionId }
        }).then(res => {
            const action = res.data;

            if (!res.success || !action) {
                // Endpoint not available — treat as timeout
                setStatus('timeout');
                return;
            }

            setActionData(action);

            if (action.status === 'completed') {
                setStatus('completed');
                if (onComplete) onComplete(action);
            } else if (action.status === 'failed') {
                setStatus('failed');
                if (onFailed) onFailed(action);
            } else {
                // Still pending
                setStatus('pending');
                attemptRef.current++;
                if (attemptRef.current < maxAttempts) {
                    const delay = intervals[Math.min(attemptRef.current, intervals.length - 1)];
                    timerRef.current = setTimeout(poll, delay);
                } else {
                    setStatus('timeout');
                }
            }
        });
    }, [actionId, intervals, maxAttempts, onComplete, onFailed]);

    // Start polling when actionId changes
    useEffect(() => {
        if (!actionId) {
            setStatus(null);
            setDismissed(false);
            return;
        }

        setStatus('pending');
        setDismissed(false);
        attemptRef.current = 0;

        // First poll after initial interval
        const delay = intervals[0];
        timerRef.current = setTimeout(poll, delay);

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [actionId]);

    // Auto-dismiss on complete/fail
    useEffect(() => {
        if (autoDismiss && (status === 'completed' || status === 'failed')) {
            const t = setTimeout(() => setDismissed(true), autoDismiss);
            return () => clearTimeout(t);
        }
    }, [status, autoDismiss]);

    // Don't render if no actionId, dismissed, or null status
    if (!actionId || dismissed || !status) return null;

    const alertClass = status === 'completed' ? 'alert-success'
        : status === 'failed' ? 'alert-danger'
        : status === 'timeout' ? 'alert-secondary'
        : `alert-${variant}`;

    const displayMessage = status === 'completed' ? successMessage
        : status === 'failed' ? (actionData?.message || 'Action failed')
        : status === 'timeout' ? 'Taking longer than expected. Try refreshing manually.'
        : message;

    return (
        <div className={`alert ${alertClass} d-flex align-items-center justify-content-between py-2 px-3 mb-2`} style={{ fontSize: '13px' }}>
            <span>
                {status === 'pending' && <span className='spinner-border spinner-border-sm me-2' role='status' />}
                {displayMessage}
            </span>
            <div>
                {(status === 'completed' || status === 'timeout') && showRefresh && onRefresh && (
                    <button className='btn btn-info btn-sm m-0 py-1 px-2' onClick={onRefresh}>
                        Refresh
                    </button>
                )}
                {status !== 'pending' && (
                    <button className='btn-close ms-2' style={{ fontSize: '10px' }} onClick={() => setDismissed(true)} aria-label='Close' />
                )}
            </div>
        </div>
    );
};

export default AsyncActionBanner;
